<template>
  <AppModalWithConfirmation
    :confirm-close="areDataChanged"
    :data-auto-testid="RENAME_STATUS_MODAL_TEST_ID"
    :data-testid="RENAME_STATUS_MODAL_TEST_ID"
    :disable-general-confirm-button="isConfirmButtonDisabled"
    :loading-general-confirm-button="loading"
    :show="show"
    :title="$t('statuses.rename_status')"
    hide-hero
    @on-close="onClose"
    @on-confirm="onRename"
  >
    <FormFieldNext :label="$t('statuses.status_name')" required>
      <AppInput
        ref="inputReference"
        v-model.trim="name"
        :data-auto-testid="RENAME_STATUS_INPUT_TEST_ID"
        :data-testid="RENAME_STATUS_INPUT_TEST_ID"
        :is-error="isNameEmpty"
        :placeholder="$t('statuses.status_name')"
        max-length="11"
        size="xlg"
        style-type="primary"
        @focus="isNameEmpty = false"
        @input="onUpdateName"
      />
      <AppFieldError v-if="isNameEmpty" :show="isNameEmpty">
        {{ $t('field.required') }}
      </AppFieldError>
    </FormFieldNext>

    <template #confirm-btn-text>
      {{ $t('action.update') }}
    </template>
  </AppModalWithConfirmation>
</template>

<script setup>
import { nextTick, watch, ref, computed, unref } from 'vue'

import RenameStatusApiHandler from '@/api/rename-status'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { STATUS_NAMES_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'

import AppModalWithConfirmation from '@/components/AppModalWithConfirmation'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppInput from '@/components/ui/AppInput/AppInput'
import {
  RENAME_STATUS_INPUT_TEST_ID,
  RENAME_STATUS_MODAL_TEST_ID
} from '@/components/workspaces/jest-jelpers'

const props = defineProps({
  show: {
    type: Boolean
  },

  statusProperties: {
    type: Object,
    required: true
  }
})

const inputReference = ref(null)
const name = ref('')

const setDefaultState = () => {
  name.value = ''
}

const emit = defineEmits({
  close: null,
  'status-renamed': null
})

watch(
  () => props.show,
  async newValue => {
    if (newValue) {
      name.value = props.statusProperties[STATUS_NAMES_ENTITY_KEYS.CUSTOMER_NAME]
      // nextTick and setTimeout(both!) are needed to make input always focused on modal window open
      await nextTick()
      setTimeout(() => {
        inputReference.value?.focus()
      }, 200)
    } else {
      setDefaultState()
    }
  },
  {
    immediate: true
  }
)

const onClose = () => {
  emit('close')
}

const areDataChanged = computed(() => {
  return name.value !== props.statusProperties[STATUS_NAMES_ENTITY_KEYS.CUSTOMER_NAME]
})

const loading = ref(false)
const isNameEmpty = ref(false)
const onRename = async () => {
  isNameEmpty.value = isStringEmpty(unref(name))
  if (unref(isNameEmpty)) {
    return
  }
  const api = new RenameStatusApiHandler()
  loading.value = true
  try {
    const payload = {
      statusId: props.statusProperties[STATUS_NAMES_ENTITY_KEYS.STATUS_ID],
      newName: unref(name)
    }
    await api.renameStatus(payload)
    emit('status-renamed', payload)
    onClose()

    tracker.logEvent('status updated', {
      category: EVENT_CATEGORIES.SETTINGS,
      label: payload.newName,
      id: payload.statusId
    })
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const onUpdateName = () => {
  name.value = name.value.replace(/[{}]/g, '')
}

const isConfirmButtonDisabled = computed(() => {
  return unref(name).trim() === props.statusProperties[STATUS_NAMES_ENTITY_KEYS.CUSTOMER_NAME]
})
</script>

<style lang="scss" scoped></style>
